import { type FC, type ReactNode, useMemo } from "react";
import { isKITProduct, type ProductData } from "../../../models/ProductData";
import { type NetPriceData, type PriceData } from "../../../models/PriceData";
import { QuantityInput } from "../../../common/components/QuantityInput/QuantityInput";
import { INVALID_PACKAGE_CODE, MAX_QUANTITY } from "../../../utils/constants";
import { PlainFormattedNumber } from "../../../common/components/PlainFormattedNumber";
import { Card } from "../../../Configurators/Linear/components/Card";
import { PriceReferenceParam } from "~/feature/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "../../../QuickEntryPopup/helpers";
import { isProductPMA, type SelectNetPriceProps } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import { Permission, userHasPermission } from "../../../utils/permissions";
import { multiply, volatileSum, zero } from "../../../utils/PriceUtils";
import { Controller, useFormContext } from "react-hook-form";
import { type AddToCartFormData } from "../../hooks/useAddToCartNew";
import { useTranslation } from "react-i18next";
import ProductImage from "~/common/components/Product/ProductImage";
import { CART_PRODUCT_IMAGE_WIDTHS } from "~/common/utils/cartConstants";
import { HousingMessages } from "~/common/components/Messages/HousingMessages";
import ProductDesignation from "~/ProductDetails/components/ProductDesignation";
import { Translation } from "~/common/Translation";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import {
  AliasesParameter,
  CountryOfOriginDescriptorParam,
  GPSDParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
  TariffParameter,
} from "~/common/components/Product/ProductParams";
import Parameter from "~/common/components/Parameter";
import { calculateSalesCampaign4PDP, type PdpSaleData } from "~/SalesCampaign/utils";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import classNames from "classnames";
import { Price as PriceV3 } from "~/common/components/Price/v3/Price";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import classes from "./AtcProductInfo.module.less";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { selectSalesPrice } from "~/SalesCampaign/selectors";
import { useSelector } from "react-redux";
import { KitPrices } from "~/ProductDetails/components/ProductSummary/components/KitPrices";
import { InfoOrderHandlingUnitQuantity } from "~/ProductDetails/components/ProductSummary/InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { isTariffSurchargeEnabled } from "~/feature/cart/cart/utils/cartUtils";

interface Props {
  variant?: "ATW" | "ATC" | "CONTRACT";
  price: NetPriceData | null;
  priceUnit: number;
  quantity: number | null;
  disabled?: boolean;
  id?: string;
  invalidQuantity?: boolean;
  onChange?: (newQty: number) => void;
  extraParams?: { label: ReactNode; value: ReactNode }[];
  priceReference: string;
  onPriceReferenceChange?: (newValue: string | undefined) => void;
  editable?: boolean;
  descriptor: ProductDescriptor;
  isPriceLoading?: boolean;
  hasSurcharge?: boolean;
  isSpecialPrice?: boolean;
  showInfoOrderHandlingUnitQty?: boolean;
}

export const AtcProductInfo = (props: Props) => {
  const {
    variant = "ATC",
    price,
    quantity,
    disabled,
    onChange,
    invalidQuantity,
    extraParams = [],
    priceUnit,
    priceReference,
    onPriceReferenceChange,
    descriptor,
    isPriceLoading,
    hasSurcharge,
    isSpecialPrice,
    showInfoOrderHandlingUnitQty,
  } = props;

  const { t } = useTranslation();
  const formProps = useFormContext<AddToCartFormData>();
  const product = descriptor.base;

  const userCanSeePrice = userHasPermission(Permission.PRICE);

  const isKIT = isKITProduct(product);

  const optionalParts = formProps?.watch("optionalParts") ?? {};

  const tariffSurcharge = isTariffSurchargeEnabled() && formProps?.watch("tariffSurcharge");

  const countTotal = (price: PriceData | null, priceUnit: number, qty: number) => {
    return !price ? null : multiply(price, qty / priceUnit);
  };

  const selectedOptionalParts = Object.values(optionalParts).filter((item) => item.selected);

  const optionalPartsPrice = volatileSum(
    ...selectedOptionalParts.map((item) => countTotal(item.netPrice, item.priceUnit, item.quantity) ?? zero()),
  );

  const optionalPartsHaveSurcharge =
    selectedOptionalParts.length > 0 && selectedOptionalParts.some((item) => item.surcharge > 0);

  const totalPrice = countTotal(price, priceUnit, quantity ?? 1) ?? zero();

  const selectedPack = formProps != null ? formProps.watch("selectedPack") : null;
  const packSize = selectedPack?.packSize ?? 1;

  const salesData = useMemo(() => calculateSalesCampaign4PDP({ productDescriptor: descriptor }), [descriptor]);
  const hasSales = salesData.data?.hasSales;

  const packCode = formProps != null ? formProps.watch("selectedPackCode") : INVALID_PACKAGE_CODE;

  const saleForPack: PdpSaleData | undefined = salesData.data?.salePerPack[packCode];
  const saleActive: boolean = !!formProps && !!saleForPack && (formProps.watch("salePriceEnabled") ?? false);

  const priceParams: SelectNetPriceProps = {
    packCode,
    extraQuantity: quantity ?? 0,
    priceReference,
    productCode: product.code,
  };
  const salePrice: PriceData = useSelector(selectSalesPrice({ ...priceParams, salesData: saleForPack })) ?? zero();
  const validSalePrice: boolean = !!salePrice && salePrice.value > 0;

  let validPrice = !!price && price.value > 0;
  if (saleActive) {
    validPrice = validPrice && price!.value !== (salePrice?.value ?? 0);
  }

  let showExtraLabels;
  if (variant === "ATC") {
    showExtraLabels = !saleActive;
  } else {
    // variant is ATW
    showExtraLabels = !salesData.data?.hasSales;
  }

  return (
    <Card className="scha__atc-product-info" disableHover={true} data-prod-article={product.hybrisCode}>
      {isPriceLoading && <CircleSpinner className={"mb-3"} />}
      {!isPriceLoading && descriptor && !isProductPMA(descriptor) && userCanSeePrice && !isKIT && (
        <div className={classNames(classes.netPriceSection, variant === "CONTRACT" && "mb-2")}>
          {!validPrice && !saleActive && (
            <span
              className={classNames({
                [classes.regularPrice]: true,
                [classes.priceOnRequest]: true,
                [classes.mainPrice]: !saleActive,
              })}
            >
              {t("product.priceOnRequest")}
            </span>
          )}
          {validPrice && (
            <PriceV3
              data-meta={"price"}
              className={classNames({
                [classes.regularPrice]: true,
                [classes.mainPrice]: !saleActive,
                [classes.active]: !saleActive,
                [classes.contractPrice]: variant === "CONTRACT",
              })}
              tariffSurcharge={tariffSurcharge}
              price={price}
            />
          )}
          {(validPrice || validSalePrice) && (
            <div className={classes.priceLabels}>
              <PriceV3.NetPriceUnit price={price} surchargeEnabled={false} />
              {tariffSurcharge && <p className="label-small mb-1">{t("price.tariff.surcharge.applicable")}</p>}
              {showExtraLabels && (
                <PriceV3.ExtraLabels className={classes.extraPriceLabels} price={price} surchargeEnabled={false} />
              )}
            </div>
          )}
          {saleActive && (
            <PriceV3
              className={classNames({
                [classes.salePrice]: true,
                [classes.active]: saleActive,
              })}
              price={salePrice}
            />
          )}
          {saleActive && (
            <SaleDiscountIndicator className={classes.discount} regularPrice={price} salePrice={salePrice} />
          )}
        </div>
      )}
      {!isPriceLoading && descriptor && !isProductPMA(descriptor) && userCanSeePrice && isKIT && (
        <KitPrices
          totalPrice={totalPrice}
          optionalPartsPrice={optionalPartsPrice}
          product={descriptor.base}
          includesSurcharge={hasSurcharge}
          optionalPartsIncludesSurcharge={optionalPartsHaveSurcharge}
        />
      )}
      {variant !== "CONTRACT" && <GPSDParameter product={product} className={"mt-2"} />}
      {hasSales && formProps && (
        <Controller
          name={"salePriceEnabled"}
          render={({ field: { value, onChange } }) => (
            <CheckboxWithLabel
              className={"mt-2"}
              checked={value}
              onCheck={onChange}
              hidden={!saleForPack || !!priceReference}
            >
              {t("feature.salesCampaign.specialDealPrice")}
            </CheckboxWithLabel>
          )}
        />
      )}
      {variant !== "CONTRACT" && (
        <PriceReferenceSection
          product={product}
          priceReference={priceReference}
          onPriceReferenceChange={onPriceReferenceChange}
          disabled={disabled || saleActive}
          editable={!isKIT && canSeePriceRefColumn()}
        />
      )}
      {quantity !== null && product.quantityVisible && (
        <>
          <QuantitySection
            packSize={packSize}
            quantity={quantity}
            onChange={onChange}
            disabled={disabled}
            invalidQuantity={invalidQuantity}
          />
          {showInfoOrderHandlingUnitQty && (
            <InfoOrderHandlingUnitQuantity
              className={"my-12"}
              preferredPackQuantity={selectedPack?.preferredPackQuantity ?? null}
              packSize={selectedPack?.packSize ?? 1}
              quantity={quantity}
            />
          )}
        </>
      )}

      <div className={"p-relative"}>
        <ProductImage product={product} widths={CART_PRODUCT_IMAGE_WIDTHS} />
        {hasSales && <SaleTag size={40} className={classes.priceTag} />}
      </div>

      <div className="scha__atc-product-info__extra">
        {isKITProduct(product) && <HousingMessages.NotFinal className={"scha__atc-product-info__extra__kit-msg"} />}
        <div className="scha__atc-product-info__extra__title">
          <ProductDesignation designation={product.designation} size={"title-medium"} isPreferred={product.preferred} />
        </div>
        <div className="scha__atc-product-info__extra__description">
          <Translation {...product.shortDescription} />
        </div>

        <NewVersionMessage className={"mt-2"} baseProduct={product} />

        <OldDesignationParameter className="mt-2" label={t("productDetailPage.oldDesignation")} product={product} />
        <SchaefflerIdParameter className="mt-2" productDescriptor={descriptor} />
        <AliasesParameter className={"mt-2"} product={product} mode={"13-digits"} />
        {extraParams.map((param, index) => (
          <Parameter className={"mt-2"} key={index} label={param.label} value={param.value} />
        ))}
        {variant === "CONTRACT" ? (
          <Parameter
            className="mt-2"
            label={t("addToCart.contract.packaging")}
            value={t("addToCartPopup.packSize", { amount: packSize, code: packCode })}
          />
        ) : (
          <TariffParameter product={product} className={"mt-2"} />
        )}
        {/* <CountryOfOriginParam product={product} className={"mt-2"} /> */}
        {descriptor && <CountryOfOriginDescriptorParam descriptor={descriptor} className={"mt-2"} />}
      </div>
    </Card>
  );
};

export default AtcProductInfo;

//////////////////////////////////////
// QUANTITY SECTION
//////////////////////////////////////
type QuantityProps = Pick<Props, "disabled" | "onChange" | "invalidQuantity"> & { quantity: number; packSize: number };
const QuantitySection: FC<QuantityProps> = ({ disabled, quantity, onChange, invalidQuantity, packSize }) => {
  const { t } = useTranslation();
  if (disabled) {
    return (
      <div className="scha__atc-product-info__quantity">
        <span className="scha__atc-product-info__quantity__label mr-1">{t("input.quantity")}</span>
        <span className="scha__atc-product-info__quantity__value">
          <PlainFormattedNumber value={quantity} />
        </span>
      </div>
    );
  }

  return (
    <QuantityInput
      className={"mt-3"}
      min={packSize}
      max={MAX_QUANTITY}
      value={quantity}
      disabled={disabled}
      step={packSize}
      data-test-id={"add-to-cart-input-quantity"}
      onValueChange={onChange}
      error={invalidQuantity}
      helperLabel={
        invalidQuantity && (
          <span className="error">
            {t("contractDetails.atc.quantityMismatch", {
              lower: Math.floor(quantity / packSize) * packSize,
              higher: Math.ceil(quantity / packSize) * packSize,
            })}
          </span>
        )
      }
      startAdornment={invalidQuantity && <Icon className={classes.errorIcon} type={IconType.ErrorCircle} />}
    />
  );
};

//////////////////////////////////////
// PRICE REFERENCE SECTION
//////////////////////////////////////
type PriceReferenceProps = Pick<Props, "priceReference" | "disabled" | "onPriceReferenceChange" | "editable"> & {
  product: ProductData;
};
const PriceReferenceSection: FC<PriceReferenceProps> = ({
  priceReference,
  onPriceReferenceChange,
  disabled,
  product,
  editable,
}) => {
  return (
    <PriceReferenceParam
      product={product}
      priceReference={priceReference}
      className={"mt-2"}
      onChange={onPriceReferenceChange}
      disabled={disabled}
      data-test-id={"add-to-cart-price-reference"}
      editable={editable}
    />
  );
};
